import { MilestoneFragmentFragment, useCreateProjectMilestone, useUpdateProjectMilestone } from '@lopo-tech/lopo-api'
import classNames from 'classnames'
import React, { useCallback, useMemo, useState } from 'react'
import InputArea from 'src/components/forms/input_area/input_area'
import InputDatePicker from 'src/components/forms/input_date_picker/input_date_picker'
import InputField from 'src/components/forms/input_field/input_field'
import Modal from 'src/components/modal/modal'
import Uploader from 'src/components/uploader/uploader'
import { CLASS_PREFIX } from 'src/constants'
import mediaSelectImage from 'src/post-creation-wizard/wizards/common/media_select/media_select.svg'
import { useCreateAssetFlow } from 'src/utils/assets/createAsset'
import DateFormatter from 'src/utils/date_formatter/date_formatter'

import { textResources } from 'src/lang/de'
import '../project_modal.scss'

type Props = {
  projectId: string;
  onCancel: () => void;
  onCreated?: () => void;

  /** If provided, the modal will prefill and update instead of create. */
  prefill?: Pick<MilestoneFragmentFragment, 'id' | 'title' | 'content' | 'images' | 'date' | 'dateLabel'>;
};

type MilestoneValues = {
  title: string;
  body: string;
  /** YYYY-MM-DD, the format both paths of InputDatePicker emit. */
  date: string;
  /** Trimmed; empty removes the label, which is why it is never undefined here. */
  dateLabel: string;
  coverImageSrc?: string;
};

const cls = `${CLASS_PREFIX}project-modal`
const t = textResources.projects

/** The backend caps the label at 80 characters, counted without surrounding spaces. */
const DATE_LABEL_MAX_LENGTH = 80

/** The calendar's own default stops two years ahead, which is short for a construction schedule. */
const CALENDAR_YEARS_AHEAD = 10
const CALENDAR_MIN_YEAR = 2000

const useCreateMilestone = () => {
  const [createProjectMilestone] = useCreateProjectMilestone()
  const createAssetFlow = useCreateAssetFlow()

  return useCallback(async (projectId: string, values: MilestoneValues) => {
    const { coverImageSrc } = values
    let assetIds: string[] = []

    if (coverImageSrc) {
      assetIds = await createAssetFlow([coverImageSrc])
    }

    const response = await createProjectMilestone({
      variables: {
        input: {
          projectId,
          title: values.title,
          body: values.body,
          date: values.date,
          // Left out when empty rather than sent as empty: a backend without the
          // field then still accepts a milestone that carries no label anyway.
          dateLabel: values.dateLabel || undefined,
          assetIds
        }
      }
    })

    return response.data?.createProjectMilestone
  }, [createProjectMilestone, createAssetFlow])
}

const useUpdateMilestone = () => {
  const [updateProjectMilestone] = useUpdateProjectMilestone()
  const createAssetFlow = useCreateAssetFlow()

  return useCallback(async (
    projectId: string,
    milestoneId: string,
    values: MilestoneValues,
    prefilled: Pick<MilestoneValues, 'date' | 'dateLabel'>,
    initialAssets?: MilestoneFragmentFragment['images']
  ) => {
    const { coverImageSrc } = values
    let assetIds = initialAssets?.map((asset) => asset.id) ?? []

    const coverImageChanged = !!coverImageSrc && coverImageSrc !== initialAssets?.[0]?.file.previewUrl
    if (coverImageChanged) {
      assetIds = await createAssetFlow([coverImageSrc])
    }

    // Only what actually changed against what the form was filled with. The form
    // is filled from the cache, which can be older than the record: a correction
    // to the title alone would otherwise write a stale day and a stale label back
    // over a newer one. An argument that is not sent never reaches the record,
    // while an empty label still removes it.
    const changedDate = values.date === prefilled.date ? undefined : values.date
    const changedLabel = values.dateLabel === prefilled.dateLabel ? undefined : values.dateLabel

    const response = await updateProjectMilestone({
      variables: {
        input: {
          id: milestoneId,
          title: values.title,
          body: values.body,
          date: changedDate,
          dateLabel: changedLabel,
          assetIds
        }
      },
      // Every page size is its own `milestones` field on `Project:<id>`, so
      // dropping the field reaches both lists. Only a saved date moves a
      // milestone in them, which is why nothing else empties them.
      update: (cache, result) => {
        const saved = result.data?.updateProjectMilestone?.projectMilestone
        if (!changedDate || !saved) {
          return
        }

        cache.evict({
          id: cache.identify({ __typename: 'Project', id: projectId }),
          fieldName: 'milestones'
        })
        cache.gc()
      }
    })

    return response.data?.updateProjectMilestone
  }, [updateProjectMilestone, createAssetFlow])
}

export const ProjectMilestoneCreateModal: React.FC<Props> = ({ projectId, onCancel, onCreated, prefill }) => {
  const initialCoverImageSrc = prefill?.images?.[0]?.file.previewUrl

  // The values the two fields start from, in the form the fields hold them, so that
  // "unchanged" can be decided by comparing strings. The label is trimmed here too:
  // what is sent is trimmed, and JavaScript's trim takes spaces that the server's
  // own strip leaves in place, among them U+00A0. Comparing an untrimmed stored
  // label against a trimmed one would call an untouched field changed and send it,
  // which is the one thing this comparison exists to avoid.
  const prefilled = useMemo(() => ({
    date: DateFormatter.dateInDatePickerFormat(prefill?.date ?? new Date()),
    dateLabel: (prefill?.dateLabel ?? '').trim()
  }), [prefill?.date, prefill?.dateLabel])

  const [imageSrc, setImageSrc] = useState<string | undefined>(initialCoverImageSrc)
  const [title, setTitle] = useState(prefill?.title ?? '')
  const [body, setBody] = useState(prefill?.content?.text ?? '')
  const [date, setDate] = useState(prefilled.date)
  const [dateLabel, setDateLabel] = useState(prefilled.dateLabel)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createMilestone = useCreateMilestone()
  const updateMilestone = useUpdateMilestone()

  const isEditMode = !!prefill?.id

  // The calendar builds its year list from minYear..maxYear only. A stored year
  // outside it is not one of the options, so the select falls back to the first
  // one while the grid still shows the stored month: the day survives and saves,
  // the year on screen is wrong. The range therefore covers the STORED year and
  // not the one currently shown, which would drop out of the list again as soon
  // as someone picks a different year.
  const [minYear, maxYear] = useMemo(() => {
    const years = [CALENDAR_MIN_YEAR, new Date().getFullYear() + CALENDAR_YEARS_AHEAD]
    const storedYear = prefill?.date ? Number(DateFormatter.asYear(prefill.date)) : Number.NaN
    if (!Number.isNaN(storedYear)) {
      years.push(storedYear)
    }

    return [Math.min(...years), Math.max(...years)]
  }, [prefill?.date])

  const values: MilestoneValues = useMemo(() => ({
    title: title.trim(),
    body: body.trim(),
    date: date.trim(),
    dateLabel: dateLabel.trim(),
    coverImageSrc: imageSrc
  }), [title, body, date, dateLabel, imageSrc])

  const canSubmit = useMemo(
    () => values.title.length > 0 && values.body.length > 0 && values.date.length > 0 && !isSubmitting,
    [values, isSubmitting]
  )

  return (
    <Modal autoSize customClassNames={`${cls}__wizard-body`} hideCloseButton>
      <>
        <div className={`${cls}__wizard-title`}>{t.createMilestoneModal.headerTitle}</div>

        <div className={`${cls}__wizard-details`}>
          {/* Left: optional image */}
          <div className={`${cls}__cover`}>
            <div className={`${cls}__cover-label`}>
              {t.createMilestoneModal.coverPhotoOptional}
            </div>
            <div
              className={classNames(`${cls}__cover-image`, {
                [`${cls}__cover-image_selected`]: !!imageSrc
              })}
            >
              <img src={imageSrc ?? mediaSelectImage} alt="" loading="lazy" />
            </div>
            <div className={`${cls}__cover-foot`}>
              <Uploader
                onImageAdded={(images) => {
                  setImageSrc(images[0])
                }}
                multiple={false}
              >
                <button type="button" className={`${cls}__browse-btn`}>
                  {t.createMilestoneModal.browseFiles}
                </button>
              </Uploader>
            </div>
          </div>

          {/* Right: details form */}
          <div className={`${cls}__form`}>
            <div className={`${cls}__form-row`}>
              <InputDatePicker
                name="milestoneDate"
                label={t.createMilestoneModal.milestoneDate}
                leftIcon={false}
                iconRight="calendar"
                value={date}
                onChange={setDate}
                minYear={minYear}
                maxYear={maxYear}
                // What the calendar's "Zurücksetzen" goes back to. Without it the
                // button reaches for today, which on an existing milestone means
                // moving it.
                defaultValue={prefill?.date}
              />
            </div>

            <div className={`${cls}__form-row`}>
              <InputField
                name="milestoneDateLabel"
                label={t.createMilestoneModal.milestoneDateLabel}
                placeholder={t.createMilestoneModal.milestoneDateLabelPlaceholder}
                assistiveText={t.createMilestoneModal.milestoneDateLabelHint}
                maxLength={DATE_LABEL_MAX_LENGTH}
                value={dateLabel}
                onChange={setDateLabel}
              />
            </div>

            <div className={`${cls}__form-row`}>
              <InputField
                name="milestoneTitle"
                label={t.createMilestoneModal.milestoneTitle}
                value={title}
                onChange={setTitle}
              />
            </div>

            <div className={`${cls}__form-row`}>
              <InputArea
                name="milestoneBody"
                label={t.createMilestoneModal.milestoneBody}
                initialRows={6}
                value={body}
                onChange={setBody}
              />
            </div>
          </div>
        </div>

        <div className={`${cls}__wizard-actions`}>
          <button type="button" className={`${cls}__back`} onClick={onCancel}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className={`${cls}__submit`}
            disabled={!canSubmit}
            onClick={async () => {
              if (!canSubmit) {
                return
              }

              try {
                setIsSubmitting(true)
                const response = isEditMode
                  ? await updateMilestone(projectId, prefill!.id, values, prefilled, prefill?.images)
                  : await createMilestone(projectId, values)

                if (!response?.errors.length) {
                  onCreated?.()
                } else {
                  alert(t.createMilestoneModal.errorGeneric)
                }
              } catch {
                alert(t.createMilestoneModal.errorGeneric)
              } finally {
                setIsSubmitting(false)
              }
            }}
          >
            {isEditMode ? t.milestones.updateMilestone : t.common.create}
          </button>
        </div>
      </>
    </Modal>
  )
}
